import { type ChatMessage } from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import { useRefs } from '@/scripts/hooks';
import { useChatContext } from '@/contexts/chat/context';
import { safeJson } from '@/scripts/utils';
import { isMeMessage } from '../useChatMessages/utils';

export interface UseGroupMessageReadersProps {
  message: ChatMessage;
}

export function useGroupMessageReaders({ message }: UseGroupMessageReadersProps) {
  const { chatGroup, chatMessages } = useChatContext();
  const [readerIds, setReaderIds] = useState<string[]>([]);
  const [receiverIds, setReceiverIds] = useState<string[]>([]);

  const isSender = useMemo(() => isMeMessage(message), [message]);

  const refs = useRefs({ chatMessages, chatGroup, message });

  const readerState = useMemo(
    () => chatGroup.chatGroupState.state.readStateMap[message.message_id],
    [chatGroup.chatGroupState.state.readStateMap, message.message_id]
  );

  const readerStateReaderIds = useMemo(
    () => readerState?.reader_ids,
    [readerState?.reader_ids]
  );

  const readerStateReceiverIds = useMemo(
    () => readerState?.expect_read_list,
    [readerState?.expect_read_list]
  );

  useEffect(() => {
    if (isSender) {
      const { chatGroup, message } = refs.current;
      (async () => {
        const readState = await chatGroup.getReadState(message.message_id);

        if (readState?.reader_ids_value) {
          setReaderIds(readState.reader_ids_value);
        }

        if (readState?.expect_read_list_value) {
          setReceiverIds(readState.expect_read_list_value);
        }
      })();
    }
  }, [refs, isSender]);

  useEffect(() => {
    if (isSender && readerStateReaderIds) {
      setReaderIds(Object.keys(safeJson(readerStateReaderIds, {})));
    } else {
      setReaderIds([]);
    }
  }, [refs, readerStateReaderIds, isSender]);

  useEffect(() => {
    if (isSender && readerStateReceiverIds) {
      setReceiverIds(readerStateReceiverIds.split(','));
    } else {
      setReceiverIds([]);
    }
  }, [refs, readerStateReceiverIds, isSender]);

  return { readerIds, receiverIds };
}
